// One process reviews the whole script: parse once, fan out over episodes
// in-memory, then converge single-threaded. Progress leaves as NDJSON so the
// caller can persist per episode without waiting for the run.
import * as fs from "node:fs";
import * as path from "node:path";
import * as sp from "@lingjingai/screenplay";
import { reviewUnit, type AgentConfig, type UnitResult, type UsageRow } from "./agent.js";
import { alignEpisode, type EpisodeAlignment } from "./asr-review.js";
import { ReviewModelError } from "./models.js";

export interface ReviewOptions {
  scriptPath: string;
  source?: string;                 // video file or episode directory
  /**
   * Explicit per-episode videos, in episode order. Takes precedence over
   * `source`: a caller that already knows the mapping should pass it rather
   * than have it re-derived here, because two orderings drift (redraw parses
   * episode numbers, including Chinese numerals; `source` only sorts names).
   */
  videos?: (string | undefined)[];
  episodes?: number[];             // default: every episode
  concurrency?: number;
  converge?: boolean;
  convergeOnly?: boolean;
  apiKey: string;
  model?: string;
  mediaModel?: string;
  baseUrl?: string;
  maxAttempts?: number;
  maxSteps?: number;
  mediaBudget?: number;
  episodeTimeoutMs?: number;
  onEvent?: (event: ReviewEvent) => void;
}

export type ReviewEvent =
  | { type: "asr-started"; episode_number: number }
  | { type: "asr-transcribed"; episode_number: number }
  | ({ type: "asr-compared"; episode_number: number } & EpisodeAlignment)
  | { type: "asr-applied"; episode_number: number; timestamp_corrections: number }
  | { type: "unit-started"; episode_number: number }
  | ({ type: "episode" | "global" } & UnitResult)
  | { type: "done"; statistics: Statistics; usage_total: UsageRow[]; wall_ms: number };

export interface Statistics {
  fixed: number; unchanged: number; unresolved: number; failed_episodes: number;
}

/** Positional pairing of sorted videos with episodes, as redraw does. */
export function episodeVideos(source: string | undefined, count: number): (string | undefined)[] {
  if (!source) return Array.from({ length: count }, () => undefined);
  const stat = fs.statSync(source);
  if (stat.isFile()) return Array.from({ length: count }, () => source);
  const files = fs.readdirSync(source)
    .filter((name) => /\.(mp4|mov|mkv|m4v)$/i.test(name))
    .sort()
    .map((name) => path.join(source, name));
  if (files.length < count) {
    throw new Error(`${source} holds ${files.length} videos for ${count} episodes`);
  }
  return files.slice(0, count);
}

function statistics(units: UnitResult[]): Statistics {
  const findings = units.flatMap((unit) => unit.findings);
  return {
    fixed: findings.filter((f) => f.outcome === "fixed").length,
    unchanged: findings.filter((f) => f.outcome === "unchanged").length,
    unresolved: findings.filter((f) => f.outcome === "unresolved").length,
    failed_episodes: units.filter((u) => u.state === "failed" && u.episode_number > 0).length,
  };
}

/** Bounded fan-out. Node is single-threaded, so an in-flight episode is just an await. */
async function pool<T>(items: T[], limit: number, run: (item: T) => Promise<void>): Promise<void> {
  const queue = [...items];
  const workers = Array.from({ length: Math.max(1, Math.min(limit, queue.length)) }, async () => {
    for (;;) {
      const item = queue.shift();
      if (item === undefined) return;
      await run(item);
    }
  });
  // Drain in-flight requests before returning a failure: callers may clean up
  // the evidence directory or retry as soon as this promise settles.
  const results = await Promise.allSettled(workers);
  const failures = results.filter((r) => r.status === "rejected");
  if (failures.length) {
    throw new Error(failures.map((r) => {
      const error: unknown = r.reason;
      return error instanceof Error ? error.message : String(error);
    }).join("\n"));
  }
}

export async function reviewScript(opts: ReviewOptions): Promise<ReviewEvent[]> {
  const started = Date.now();
  // Parse once. Every tool mutates this document in memory; nothing is written
  // until commit, so there is no lock and no plan digest to arbitrate writers.
  const doc = sp.parse(JSON.parse(fs.readFileSync(opts.scriptPath, "utf8")));
  const config: AgentConfig = {
    apiKey: opts.apiKey,
    model: opts.model ?? "gemini-3.8-flash",
    mediaModel: opts.mediaModel ?? opts.model ?? "gemini-3.8-flash",
    ...(opts.baseUrl ? { baseUrl: opts.baseUrl } : {}),
    maxAttempts: opts.maxAttempts ?? 3,
    maxSteps: opts.maxSteps ?? 300,
    mediaBudget: opts.mediaBudget ?? 1,
    timeoutMs: opts.episodeTimeoutMs ?? 8 * 60_000,
  };

  const all = doc.episodes.map((ep) => ep.no);
  const selected = opts.convergeOnly ? [] : opts.episodes?.length ? opts.episodes.filter((n) => all.includes(n)) : all;
  const videos = opts.videos?.length
    ? opts.videos
    : episodeVideos(opts.source, doc.episodes.length);
  const events: ReviewEvent[] = [];
  const emit = (event: ReviewEvent) => { events.push(event); opts.onEvent?.(event); };

  const alignments = new Map<number, EpisodeAlignment>();
  let timestampCorrections = 0;

  const units: UnitResult[] = [];
  // A reported unit must be a durable one: the caller treats a line as "this
  // episode is done" and will not re-run it, so the document reaches disk
  // before the line goes out. Committing once at the end instead made a
  // crashed run lose every edit while its already-reported findings still
  // claimed them.
  //
  // The snapshot covers the whole document, so it also carries whatever the
  // still-running episodes have changed so far. That is unavoidable with one
  // shared document and harmless: mutations are synchronous, so a commit can
  // only land between two tool calls, never inside one. An episode that never
  // reported is re-run, and the agent re-derives what to fix from the current
  // text rather than replaying a plan — which is why this needs no digest.
  const checkpoint = (unit: UnitResult, type: "episode" | "global") => {
    units.push(unit);
    sp.commit(doc, opts.scriptPath);
    emit({ type, ...unit });
  };

  await pool(selected, opts.concurrency ?? 5, async (episode) => {
    const episodeStarted = Date.now();
    const video = videos[episode - 1];
    let alignment: EpisodeAlignment | undefined;
    if (opts.source || opts.videos?.length) {
      emit({ type: "asr-started", episode_number: episode });
      // An upstream failure belongs to this episode; queued episodes must
      // still run. Disk commits remain outside this boundary and fail loudly.
      try {
        if (!video) throw new Error(`第 ${episode} 集缺少原视频`);
        alignment = await alignEpisode(doc, episode, video, opts.scriptPath, config,
          () => emit({ type: "asr-transcribed", episode_number: episode }));
      } catch (error) {
        checkpoint({ episode_number: episode, state: "failed", message: "ASR 对比失败",
          error: error instanceof Error ? error.message : String(error), findings: [],
          wall_ms: Date.now() - episodeStarted, steps: 0, media_calls: 0,
          usage: error instanceof ReviewModelError ? error.usage : [] }, "episode");
        return;
      }
      alignments.set(episode, alignment);
      emit({ type: "asr-compared", episode_number: episode, ...alignment });
      const corrections = alignment.report.timestamp_corrections;
      sp.retime(doc, corrections.map((fix) => ({ at: fix.action, ts: fix.after })));
      sp.commit(doc, opts.scriptPath);
      timestampCorrections += corrections.length;
      emit({ type: "asr-applied", episode_number: episode, timestamp_corrections: corrections.length });
    }
    emit({ type: "unit-started", episode_number: episode });
    checkpoint(await reviewUnit(doc, episode, video, config, alignment?.report), "episode");
  });

  // Convergence runs alone and last: it is the only phase allowed to touch
  // global assets, which is what keeps two episodes from merging the same
  // duplicate actor concurrently.
  if (opts.convergeOnly || opts.converge !== false) {
    emit({ type: "unit-started", episode_number: 0 });
    checkpoint(await reviewUnit(doc, 0, undefined, config), "global");
  }

  const usage_total = [...alignments.values()].flatMap((result) => result.usage).concat(units.flatMap((unit) => unit.usage));
  const stats = statistics(units);
  stats.fixed += timestampCorrections;
  emit({
    type: "done",
    statistics: stats,
    usage_total,
    wall_ms: Date.now() - started,
  });
  return events;
}
